// headerMatching.jsx
import React, { useState } from 'react';
import { parseHeadersFromFile } from '../utils/parseHeaders';

export default function HeaderUploader({
  onFileSelected,          // (file) => void
  onPreview,               // ({type, headers, rows}) => void
  onError,                 // (msg) => void
  onBusyChange,            // (boolean) => void   <-- NEW
  accept = '.csv,.xlsx,.xls,.json,.jsonl,.zip',
  sampleRows = 200,
  maxBytes = 1000 * 1024 * 1024, // 500MB
}) {
  const [preview, setPreview] = useState(null);
  const [parseErr, setParseErr] = useState('');
  const [file, setFile] = useState(null);
  
  function InfoTooltip({ text }) {
      return (
        <span className="relative group inline-block ml-1">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-pointer" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 11-0 20 10 10 0 010-20z"/>
          </svg>
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 z-10">
            {text}
          </div>
        </span>
      );
    }

  const humanMB = (b) => Math.round(b / (1024 * 1024));

  const handleChange = async (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setPreview(null);
    setParseErr('');
    onError?.('');

    if (!f) return;

    // size check (preserved)
    if (f.size > maxBytes) {
      const msg = `File size must be < ${humanMB(maxBytes)}MB`;
      setParseErr(msg);
      onError?.(msg);
      onFileSelected?.(null);
      return;
    }

    // let parent keep the file for submit
    onFileSelected?.(f);

    // ZIP: skip preview/parse, no loader needed
    const ext = (f.name.split('.').pop() || '').toLowerCase();
    if (ext === 'zip') {
      setPreview(null);
      return;
    }

     // Optional: skip preview for very large files; avoid main-thread work now
    // (You can tune this threshold)
    // const SKIP_PREVIEW_BYTES = 50 * 1024 * 1024; // 50MB
    // if (f.size > SKIP_PREVIEW_BYTES) {
    //   // Just show filename; parent can later parse/canonicalize in a worker
    //   onPreview?.({ type: ext, headers: [], rows: [] });
    //   return;
    // }


    // show global loader while parsing
    onBusyChange?.(true);
    try {
      // const out = await parseHeadersFromFile(f, sampleRows);
      // setPreview(out);
      // onPreview?.(out);
           // Force a small preview no matter what the caller passes
      const LIMIT = Math.min(sampleRows || 200, 200);
      const out = await parseHeadersFromFile(f, LIMIT);
      // Keep only headers + tiny sample in state to avoid huge memory
      const tiny = {
        type: out.type,
        headers: out.headers || [],
        rows: (out.rows || []).slice(0, LIMIT),
      };
      setPreview(tiny);
      onPreview?.(tiny);
    } catch (ex) {
      const msg = ex?.message || 'Failed to parse file';
      setParseErr(msg);
      onError?.(msg);
    } finally {
      onBusyChange?.(false);
    }
  };

  return (
      <div className="rounded-xl border border-gray-200 bg-white/90 shadow-sm">
        {/* top accent */}
        <div className="h-1 w-full bg-gradient-to-r from-purple-600 via-purple-500 to-orange-500 rounded-t-xl" />

        <div className="p-5 md:p-6">
          {/* header */}
          <div className="flex items-start justify-between mb-4">
            <h2 className="text-base md:text-lg font-semibold text-purple-900 flex items-center gap-2">
              Data Upload
              <InfoTooltip text="ZIP archives must contain only one file type — either all .csv files or all .xlsx files." />
            </h2>
            <span className="hidden md:inline text-[11px] text-gray-500">CSV, XLSX, ZIP</span>
          </div>

          {/* dropzone */}
          <div className="relative">
            <input
              type="file"
              accept={accept}
              onChange={handleChange}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />

            <div className="group border-2 border-dashed border-gray-300 rounded-lg p-6 text-center transition-colors hover:border-orange-400 bg-white">
              <div className="mx-auto mb-2 h-10 w-10 rounded-full bg-purple-50 flex items-center justify-center group-hover:scale-105 transition">
                {/* folder-arrow icon */}
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple-600" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7a2 2 0 012-2h4l2 2h6a2 2 0 012 2v1M8 15l4-4 4 4M12 11v8" />
                </svg>
              </div>

              <p className="text-sm text-gray-900 font-medium">
                Upload CSV , Excel or ZIP file
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Click to browse or drag & drop your file here
              </p>

              {/* badges */}
              <div className="mt-3 flex items-center justify-center gap-2">
                <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-700">
                  .csv • .xlsx • .zip
                </span>
                <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-700">
                  1 file / upload
                </span>
              </div>
            </div>

            {/* selected file */}
            {file && (
              <div className="mt-3 flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-3">
                <span className="mt-0.5 text-green-600">✓</span>
                <div className="min-w-0">
                  <p className="text-sm text-green-800 truncate">{file.name}</p>
                  <p className="text-[11px] text-green-700/80">
                    {(file.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                </div>
              </div>
            )}

            {/* error */}
            {parseErr && (
              <p className="mt-2 text-xs text-red-600">{parseErr}</p>
            )}
          </div>

          {/* (optional) inline header preview — keep hidden unless you want it */}
          {preview && (
            <div className="mt-4">
              {/* If you later want to show a compact header list, uncomment and style it similarly to other cards */}
              {/* <p className="text-xs text-gray-600 mb-1">Detected headers</p> */}
            </div>
          )}

          {/* helper line */}
          {/* <p className="mt-3 text-[11px] text-gray-500">
            Tip: For ZIP uploads, include only one file type inside (all CSV <span className="mx-0.5">or</span> all XLSX).
          </p> */}
        </div>
      </div>

  );
}
